import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  VerifiedUser,
  History,
  TrendingUp,
  Business,
  Add,
  CheckCircle,
  Description,
  CloudUpload,
  Architecture,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { ChantierService } from '../services/chantierService';
import { Chantier } from '../types';

const PromoteurDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [chantiers, setChantiers] = useState<Chantier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChantiers = async () => {
      if (!user) return;
      try {
        const data = await ChantierService.getChantiersByDeveloper(user.id);
        setChantiers(data);
      } catch (error) {
        console.error('Failed to fetch chantiers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChantiers();
  }, [user]);

  if (loading) {
    return <LinearProgress />;
  }

  // Calculate stats based on real data
  const deliveredCount = chantiers.filter(c => c.progress.phase === 'delivered').length;
  const trustScore = deliveredCount > 0 ? (deliveredCount / chantiers.length * 10).toFixed(1) : "N/A";
  const velocity = chantiers.length > 0 ? "Live Tracking" : "0%";

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: 600, color: 'text.primary', fontFamily: '"Playfair Display", serif' }}>
            Brand Headquarters
          </Typography>
          {user?.profile.company && (
            <Chip 
              icon={<VerifiedUser />} 
              label="Verified Builder" 
              color="primary" 
              variant="outlined" 
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          size="large"
          sx={{ boxShadow: 'none' }}
          onClick={() => navigate('/chantiers/create')}
        >
          New Chantier
        </Button>
      </Box>

      <Grid container spacing={4}>
        {/* Stats Section */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: 'white', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUp color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Construction Velocity</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontFamily: '"Playfair Display", serif' }}>
                    {velocity}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overall project delivery speed vs. schedule
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: 'white', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CheckCircle color="success" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Trust Score</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontFamily: '"Playfair Display", serif' }}>
                    {trustScore}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Based on on-time delivery and buyer feedback
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Active Projects */}
          <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, fontFamily: '"Playfair Display", serif' }}>
            Active Chantiers
          </Typography>
          <Grid container spacing={3}>
            {chantiers.length > 0 ? chantiers.map((chantier) => (
              <Grid item xs={12} key={chantier.id}>
                <Card 
                  sx={{ display: 'flex', p: 0, overflow: 'hidden', cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                  onClick={() => navigate(`/chantiers/${chantier.id}`)}
                >
                  <Box 
                    sx={{ 
                      width: 200, 
                      bgcolor: 'grey.200',
                      backgroundImage: `url(${chantier.heroVideoUrl || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=2070'})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }} 
                  />
                  <CardContent sx={{ flex: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{chantier.name}</Typography>
                      <Chip label={`${chantier.progress.percentage}% Complete`} size="small" color="primary" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {chantier.description.substring(0, 100)}...
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>Construction Progress ({chantier.progress.phase})</Typography>
                      <LinearProgress variant="determinate" value={chantier.progress.percentage} sx={{ height: 6, borderRadius: 3, mb: 2 }} />
                    </Box>

                    <Divider sx={{ my: 2, opacity: 0.5 }} />
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        startIcon={<Add />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/properties/create?chantierId=${chantier.id}`);
                        }}
                      >
                        Add Unit
                      </Button>
                      <Button 
                        size="small" 
                        variant="text" 
                        startIcon={<Description />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/chantiers/${chantier.id}`);
                        }}
                      >
                        Manage
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )) : (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 10, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2, border: '1px dashed rgba(0,0,0,0.1)' }}>
                  <Architecture sx={{ fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.3 }} />
                  <Typography variant="h6" gutterBottom>No projects launched yet</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Start by creating your first Chantier Master Page to showcase your project to the world.
                  </Typography>
                  <Button variant="outlined" startIcon={<Add />} onClick={() => navigate('/chantiers/create')}>
                    Create First Project
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>Historical Portfolio</Typography>
              <List>
                {chantiers.filter(c => c.progress.phase === 'delivered').length > 0 ? (
                  chantiers.filter(c => c.progress.phase === 'delivered').map(c => (
                    <ListItem key={c.id} disableGutters>
                      <ListItemIcon><History /></ListItemIcon>
                      <ListItemText 
                        primary={c.name} 
                        secondary={`Delivered • ${c.units.length} Units`}
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No delivered projects yet.</Typography>
                )}
              </List>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: 'primary.main', color: 'white', mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Business />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Company Profile</Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 3 }}>
                {user?.profile.company || 'Company details not set.'}
              </Typography>
              <Button 
                variant="contained" 
                sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }} 
                fullWidth
                onClick={() => navigate('/settings')}
              >
                Update Profile
              </Button>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderStyle: 'dashed', borderColor: 'primary.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Description color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Verification & Compliance</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Upload your Trade Register (Registre du Commerce) and professional liability proof to unlock the "Verified Builder" seal.
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<CloudUpload />} 
                fullWidth
                sx={{ py: 1 }}
              >
                Upload Documents
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PromoteurDashboardPage;